import { AppState } from '@/context/appContext';
import { canvasCoordinatesToPdfCoordinates } from '@/helpers';
import { FormField, FormTypes, PageMetadata, TextFormField } from '@/types';

export enum FormsActionTypes {
  SET_FORM_FIELD_TYPE_TO_INSERT = 'SET_FORM_FIELD_TYPE_TO_INSERT',
  ADD_FORM_FIELD = 'ADD_FORM_FIELD',
  DUPLICATE_FORM_FIELD = 'DUPLICATE_FORM_FIELD',
  UPDATE_FORM_FIELD = 'UPDATE_FORM_FIELD',
  SELECT_FORM_FIELD = 'SELECT_FORM_FIELD',
  DELETE_FORM_FIELD = 'DELETE_FORM_FIELD',
}

export type FormsReducerAction =
  | {
      type: FormsActionTypes.SET_FORM_FIELD_TYPE_TO_INSERT;
      payload: { type: FormTypes | null };
    }
  | {
      type: FormsActionTypes.ADD_FORM_FIELD;
      payload: { formField: FormField; pageNumber: number };
    }
  | {
      type: FormsActionTypes.UPDATE_FORM_FIELD;
      payload: {
        updatedField: Partial<Omit<FormField, 'id'>> & { id: FormField['id'] };
        pageNumber: number;
        page: PageMetadata;
        scale: number;
      };
    }
  | {
      type: FormsActionTypes.SELECT_FORM_FIELD;
      payload: { id: string | null };
    }
  | {
      type: FormsActionTypes.DELETE_FORM_FIELD;
      payload: { id: string; pageNumber: number };
    }
  | {
      type: FormsActionTypes.DUPLICATE_FORM_FIELD;
      payload: { id: string; newId: string; pageNumber: number };
    };

const formsReducer = (state: AppState, action: FormsReducerAction) => {
  switch (action.type) {
    case FormsActionTypes.SET_FORM_FIELD_TYPE_TO_INSERT: {
      return {
        ...state,
        formFieldTypeToInsert: action.payload.type,
      };
    }
    case FormsActionTypes.ADD_FORM_FIELD: {
      const newFormField: FormField = {
        ...action.payload.formField,
      };

      newFormField.name = `${newFormField.type}_${newFormField.id}`;

      return {
        ...state,
        formFields: {
          ...state.formFields,
          [action.payload.pageNumber]: [...(state.formFields[action.payload.pageNumber] ?? []), newFormField],
        },
      };
    }
    case FormsActionTypes.UPDATE_FORM_FIELD: {
      const { updatedField, pageNumber, page, scale } = action.payload;
      if (updatedField.canvasCoordinates)
        updatedField.pdfCoordinates = canvasCoordinatesToPdfCoordinates(
          updatedField.canvasCoordinates,
          page.page,
          scale,
        );

      const formFields = state.formFields[pageNumber];

      const updatedFormFields = formFields.map((field) => {
        if (field.id === updatedField.id) {
          return {
            ...field,
            ...updatedField,
          };
        }
        return field;
      });

      return {
        ...state,
        formFields: {
          ...state.formFields,
          [pageNumber]: updatedFormFields,
        },
      };
    }
    case FormsActionTypes.SELECT_FORM_FIELD: {
      return {
        ...state,
        selectedFormFieldId: action.payload.id,
      };
    }
    case FormsActionTypes.DELETE_FORM_FIELD: {
      const updatedFormFields = state.formFields[action.payload.pageNumber].filter(
        (field) => field.id !== action.payload.id,
      );

      return {
        ...state,
        formFields: {
          ...state.formFields,
          [action.payload.pageNumber]: updatedFormFields,
        },
      };
    }
    case FormsActionTypes.DUPLICATE_FORM_FIELD: {
      const { id, newId, pageNumber } = action.payload;
      const formField = state.formFields[pageNumber].find((field) => field.id === id);

      if (!formField) return state;

      const newFormField: TextFormField = {
        ...formField,
        id: newId,
        canvasCoordinates: {
          ...formField.canvasCoordinates,
          x: formField.canvasCoordinates.x + 20,
          y: formField.canvasCoordinates.y + 20,
        },
        existing: false,
      };

      newFormField.name = `${newFormField.type}_${newFormField.id}`;

      return {
        ...state,
        formFields: {
          ...state.formFields,
          [action.payload.pageNumber]: [...state.formFields[action.payload.pageNumber], newFormField],
        },
      };
    }
    default:
      return state;
  }
};

export default formsReducer;
